/**
 * Hooks backing RecordSaleDialog — split out to keep the component itself a
 * thin composition of dialog chrome + breakdown body (sale-breakdown-not-
 * accepted-units / "Sale action in the offer workspace").
 */
import * as React from 'react';
import { ApiErrorCode } from '@ooh/contracts';
import type { CampaignOutcomeItem, Offer } from '@ooh/contracts';
import { toast } from 'sonner';
import type { TFunction } from 'i18next';
import { useMarkOfferSold } from '../api';
import type { UseSalePreviewResult } from '../api';
import type { UseOfferUnitsAllResult } from '../units-tab/api/queries';
import { handleFinalizeMutationError } from './finalize-mutation-error';
import { getSaleRowOutcome, type SaleBreakdownRow } from './record-sale-columns';

export interface SaleBreakdownState {
  isLoading: boolean;
  isError: boolean;
  rows: SaleBreakdownRow[];
  notCurrentlyAcceptedCount: number;
  totalCount: number;
  closingCount: number;
  skippedCount: number;
}

/**
 * Joins the sale-preview rows against the offer's full unit list by
 * `mediaUnitId` (the preview response carries only `{mediaUnitId,
 * currentlyAccepted, periods, reservationStatus}` — the mirrored columns
 * need the rest of `OfferUnitItem`) and derives the closing/skipped counts
 * for the caller's current opt-in choice.
 */
export function useSaleBreakdown(
  salePreview: UseSalePreviewResult,
  allUnits: UseOfferUnitsAllResult,
  includeNotCurrentlyAccepted: boolean,
): SaleBreakdownState {
  const offerUnitsById = React.useMemo(
    () => new Map((allUnits.data ?? []).map((unit) => [unit.mediaUnitId, unit])),
    [allUnits.data],
  );

  const rows: SaleBreakdownRow[] = React.useMemo(() => {
    if (!salePreview.data) return [];
    const result: SaleBreakdownRow[] = [];
    for (const entry of salePreview.data.rows) {
      const unit = offerUnitsById.get(entry.mediaUnitId);
      // Defensive: the unit was removed from the offer between the two reads.
      if (!unit) continue;
      result.push({
        ...unit,
        currentlyAccepted: entry.currentlyAccepted,
        periods: entry.periods,
        reservationStatus: entry.reservationStatus,
      });
    }
    return result;
  }, [salePreview.data, offerUnitsById]);

  // Derived from the same joined `rows` array the table renders — never from
  // the raw, unfiltered preview response — so a stat card can never disagree
  // with the row set actually listed below it (e.g. when a unit is dropped
  // by the defensive join-miss skip above).
  const totalCount = rows.length;
  // Excludes already-sold rows: the include opt-in can never move one of
  // those into the sale, so it must never drive that checkbox's warning/count.
  // Passes includeNotCurrentlyAccepted=false so this count reflects the
  // underlying not-accepted set, unaffected by the caller's current toggle
  // state — delegates the "already sold" check to getSaleRowOutcome instead
  // of re-implementing it, so the two can never drift apart.
  const notCurrentlyAcceptedCount = rows.filter(
    (row) => getSaleRowOutcome(row, false) === 'skipped',
  ).length;
  const closingCount = rows.filter(
    (row) => getSaleRowOutcome(row, includeNotCurrentlyAccepted) === 'willClose',
  ).length;
  const skippedCount = rows.filter(
    (row) => getSaleRowOutcome(row, includeNotCurrentlyAccepted) === 'skipped',
  ).length;

  return {
    isLoading: salePreview.isLoading || allUnits.isLoading,
    isError: salePreview.isError,
    rows,
    notCurrentlyAcceptedCount,
    totalCount,
    closingCount,
    skippedCount,
  };
}

/** Distinct-nośnik count for one outcome bucket — a nośnik held across several
 * windows must never count more than once for the same outcome. */
function countDistinctUnits(
  results: CampaignOutcomeItem[],
  outcome: CampaignOutcomeItem['outcome'],
): number {
  return new Set(results.filter((r) => r.outcome === outcome).map((r) => r.mediaUnitId)).size;
}

export function useRecordSaleHandler(
  offer: Offer,
  onClose: () => void,
  includeNotCurrentlyAccepted: boolean,
  t: TFunction<'offers'>,
) {
  const markOfferSold = useMarkOfferSold();

  const handleRecordSale = React.useCallback(async () => {
    try {
      const soldOffer = await markOfferSold.mutateAsync({
        id: String(offer.id),
        includeNotCurrentlyAccepted,
      });
      onClose();
      // Count distinct nośniki, never reservation windows — a nośnik held
      // across several windows must never be double-counted here.
      const soldCount = countDistinctUnits(soldOffer.sale.results, 'sold');
      const notClosedCount = countDistinctUnits(soldOffer.sale.results, 'not_closed');
      const skippedNotAcceptedCount = countDistinctUnits(
        soldOffer.sale.results,
        'skipped_not_accepted',
      );

      if (soldCount === 0) {
        // A call that closed nothing avoids reporting a zero count as the result.
        toast.success(t('workspace.header.toasts.saleRecordedZero'));
      } else {
        toast.success(t('workspace.header.toasts.saleRecorded', { count: soldCount }));
      }
      if (notClosedCount > 0) {
        toast.warning(t('workspace.header.toasts.salePartial', { count: notClosedCount }));
      }
      // Reported separately from salePartial — skipped-by-choice and
      // not-closed-due-to-conflict are different concepts.
      if (skippedNotAcceptedCount > 0) {
        toast.warning(t('workspace.header.toasts.saleSkipped', { count: skippedNotAcceptedCount }));
      }
    } catch (error) {
      handleFinalizeMutationError(error, t, {
        conflictCode: ApiErrorCode.CAMPAIGN_NOT_CONFIRMED,
        conflictToastKey: 'workspace.header.toasts.saleConflict',
        forbiddenToastKey: 'workspace.header.toasts.saleForbidden',
        genericToastKey: 'workspace.header.toasts.saleError',
      });
    }
  }, [offer.id, includeNotCurrentlyAccepted, onClose, t, markOfferSold]);

  return { markOfferSold, handleRecordSale };
}
